import React, { useState, useEffect } from 'react';
import budgetMarginRulesService from '../services/budgetMarginRulesService';
import productsService from '../services/productsService';
import toast from 'react-hot-toast';
import { Plus, Trash2, Edit2, X, Settings } from 'lucide-react';
import ConfirmModal from './ConfirmModal';

const BudgetMarginRulesManager = () => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [products, setProducts] = useState([]);
  const [confirmAction, setConfirmAction] = useState({ show: false, onConfirm: null, message: '' });

  const [formData, setFormData] = useState({
    scope: 'GLOBAL',
    category: '',
    produto_id: '',
    margin_type: 'PERCENTAGE',
    margin_value: '',
    priority: 0
  });

  const categories = ['MATERIA_PRIMA', 'FERRAGENS', 'INSUMOS', 'SERVICOS', 'LOGISTICA', 'OUTROS'];

  useEffect(() => {
    loadRules();
    loadProducts();
  }, []);

  const loadRules = async () => {
    try {
      const response = await budgetMarginRulesService.getAll();
      if (response.success) setRules(response.data);
    } catch (error) {
      console.error('Erro ao carregar regras:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      const response = await productsService.getAll({ limit: 100 });
      if (response.data) setProducts(response.data);
    } catch (error) {
      console.error('Erro ao carregar produtos:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const data = {
      ...formData,
      margin_value: parseFloat(formData.margin_value) || 0,
      priority: parseInt(formData.priority) || 0
    };

    try {
      if (editingRule) {
        await budgetMarginRulesService.update(editingRule.id, data);
        toast.success('Regra atualizada!');
      } else {
        await budgetMarginRulesService.create(data);
        toast.success('Regra criada!');
      }

      resetForm();
      loadRules();
    } catch (error) {
      toast.error('Erro ao salvar regra');
    }
  };

  const handleDelete = async (id) => {
    setConfirmAction({
      show: true,
      message: 'Excluir esta regra?',
      onConfirm: async () => {
        try {
          await budgetMarginRulesService.delete(id);
          toast.success('Regra excluída!');
          loadRules();
        } catch (error) {
          toast.error('Erro ao excluir regra');
        }
      }
    });
  };

  const handleEdit = (rule) => {
    setEditingRule(rule);
    setFormData({
      scope: rule.scope,
      category: rule.category || '',
      produto_id: rule.produto_id || '',
      margin_type: rule.margin_type,
      margin_value: rule.margin_value,
      priority: rule.priority
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      scope: 'GLOBAL',
      category: '',
      produto_id: '',
      margin_type: 'PERCENTAGE',
      margin_value: '',
      priority: 0
    });
    setEditingRule(null);
    setShowForm(false);
  };

  const getScopeLabel = (scope) => {
    switch (scope) {
      case 'GLOBAL': return 'Global (Todos)';
      case 'CATEGORY': return 'Por Categoria';
      case 'PRODUCT': return 'Por Produto';
      default: return scope;
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Settings className="w-7 h-7" />
            Regras de Margem - Orçamento
          </h1>
          <p className="text-gray-600 mt-1">
            Configure margens de lucro para itens do orçamento
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
        >
          <Plus className="w-4 h-4" />
          Nova Regra
        </button>
      </div>

      {/* Formulário */}
      {showForm && (
        <div className="bg-white border rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">
            {editingRule ? 'Editar Regra' : 'Nova Regra de Margem'}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Escopo *</label>
                <select
                  value={formData.scope}
                  onChange={(e) => setFormData(prev => ({ ...prev, scope: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2"
                  required
                >
                  <option value="GLOBAL">Global (Todos)</option>
                  <option value="CATEGORY">Por Categoria</option>
                  <option value="PRODUCT">Por Produto</option>
                </select>
              </div>

              {formData.scope === 'CATEGORY' && (
                <div>
                  <label className="block text-sm font-medium mb-1">Categoria *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2"
                    required
                  >
                    <option value="">Selecione...</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              )}

              {formData.scope === 'PRODUCT' && (
                <div>
                  <label className="block text-sm font-medium mb-1">Produto *</label>
                  <select
                    value={formData.produto_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, produto_id: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2"
                    required
                  >
                    <option value="">Selecione...</option>
                    {products.map(p => (
                      <option key={p.id} value={p.id}>{p.nome}</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-1">Tipo Margem *</label>
                <select
                  value={formData.margin_type}
                  onChange={(e) => setFormData(prev => ({ ...prev, margin_type: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2"
                  required
                >
                  <option value="PERCENTAGE">Percentual (%)</option>
                  <option value="FIXED">Valor Fixo (R$)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Valor {formData.margin_type === 'PERCENTAGE' ? '(%)' : '(R$)'} *
                </label>
                <input
                  type="number"
                  value={formData.margin_value}
                  onChange={(e) => setFormData(prev => ({ ...prev, margin_value: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2"
                  step={formData.margin_type === 'PERCENTAGE' ? '0.1' : '0.01'}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Prioridade</label>
                <input
                  type="number"
                  value={formData.priority}
                  onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 border rounded-lg"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg"
              >
                Salvar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Lista de regras */}
      <div className="bg-white rounded-lg shadow">
        {loading ? (
          <div className="p-8 text-center">Carregando...</div>
        ) : rules.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            Nenhuma regra cadastrada
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-4 py-3">Escopo</th>
                <th className="text-left px-4 py-3">Aplicação</th>
                <th className="text-left px-4 py-3">Tipo</th>
                <th className="text-right px-4 py-3">Valor</th>
                <th className="text-center px-4 py-3">Prioridade</th>
                <th className="text-center px-4 py-3">Status</th>
                <th className="text-center px-4 py-3">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {rules.map(rule => (
                <tr key={rule.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <span className="px-2 py-1 bg-gray-100 rounded text-sm">
                      {getScopeLabel(rule.scope)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {rule.scope === 'GLOBAL' && 'Todos os produtos'}
                    {rule.scope === 'CATEGORY' && rule.category}
                    {rule.scope === 'PRODUCT' && (rule.produto_nome || `Produto #${rule.produto_id}`)}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {rule.margin_type === 'PERCENTAGE' ? '%' : 'R$ Fixo'}
                  </td>
                  <td className="px-4 py-3 text-right font-medium">
                    {rule.margin_type === 'PERCENTAGE'
                      ? `${rule.margin_value}%`
                      : `R$ ${rule.margin_value}`}
                  </td>
                  <td className="px-4 py-3 text-center">{rule.priority}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-1 rounded text-xs ${
                      rule.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {rule.is_active ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => handleEdit(rule)}
                      className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(rule.id)}
                      className="p-1 text-red-600 hover:bg-red-50 rounded ml-1"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            </table>
        )}
      </div>

      <ConfirmModal
        isOpen={confirmAction.show}
        message={confirmAction.message}
        onClose={() => setConfirmAction({ show: false, onConfirm: null, message: '' })}
        onConfirm={confirmAction.onConfirm}
      />
    </div>
  );
};

export default BudgetMarginRulesManager;
